import type { LookupItem } from './criminalCaseRequests';
import { getDistricts, getProvinces } from './criminalCaseRequests';
import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';
import { parseCourtTypesFromApi } from '../utils/courtTypeLookup';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const COURT_NAME_MAX_LENGTH = 100;

export type CourtListRow = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  courtTypeId: string;
  courtTypeName: string;
  divisionId: string;
  divisionName: string;
  provinceId: string;
  provinceName: string;
  districtId: string;
  districtName: string;
  orderIndex: number;
};

export type CourtDetail = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  orderIndex: number;
  courtTypeId: string;
  courtTypeName: string;
  divisionId: string;
  divisionName: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type CourtListQuery = {
  active?: boolean;
  name?: string;
  courtTypeId?: string;
  divisionId?: string;
  districtId?: string;
  provinceId?: string;
};

export type CreateCourtInput = {
  name: string;
  active: boolean;
  courtTypeId: string;
  divisionId: string;
};

export type UpdateCourtInput = CreateCourtInput;

type ApiCourtRow = Record<string, unknown>;

function mapCourtListRow(row: ApiCourtRow): CourtListRow | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(row.code),
    active: bool(row.active) ?? true,
    courtTypeId: str(row.court_type_id ?? row.courtTypeId),
    courtTypeName: str(row.court_type_name ?? row.courtTypeName),
    divisionId: str(row.division_id ?? row.divisionId),
    divisionName: str(row.division_name ?? row.divisionName),
    provinceId: str(row.province_id ?? row.provinceId),
    provinceName: str(row.province_name ?? row.provinceName),
    districtId: str(row.district_id ?? row.districtId),
    districtName: str(row.district_name ?? row.districtName),
    orderIndex: typeof row.order_index === 'number' ? row.order_index : typeof row.orderIndex === 'number' ? row.orderIndex : 0,
  };
}

function mapCourtDetail(row: ApiCourtRow): CourtDetail | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(row.code),
    active: bool(row.active) ?? true,
    orderIndex: typeof row.order_index === 'number' ? row.order_index : typeof row.orderIndex === 'number' ? row.orderIndex : 0,
    courtTypeId: str(row.court_type_id ?? row.courtTypeId),
    courtTypeName: str(row.court_type_name ?? row.courtTypeName),
    divisionId: str(row.division_id ?? row.divisionId),
    divisionName: str(row.division_name ?? row.divisionName),
    createdAt: str(row.created_at ?? row.createdAt),
    createdBy: str(row.created_by ?? row.createdBy),
    updatedAt: str(row.updated_at ?? row.updatedAt),
    updatedBy: str(row.updated_by ?? row.updatedBy),
  };
}

function unwrapRows(data: unknown): ApiCourtRow[] {
  if (Array.isArray(data)) return data as ApiCourtRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.content)) return record.content as ApiCourtRow[];
  if (Array.isArray(record.data)) return record.data as ApiCourtRow[];
  return [];
}

function buildCourtListQuery(query: CourtListQuery): string {
  const qs = new URLSearchParams();
  if (typeof query.active === 'boolean') qs.set('active', String(query.active));
  const name = query.name?.trim();
  if (name) qs.set('name', name);
  const courtTypeId = query.courtTypeId?.trim();
  if (courtTypeId) qs.set('courtTypeId', courtTypeId);
  const divisionId = query.divisionId?.trim();
  if (divisionId) qs.set('divisionId', divisionId);
  const districtId = query.districtId?.trim();
  if (districtId) qs.set('districtId', districtId);
  const provinceId = query.provinceId?.trim();
  if (provinceId) qs.set('provinceId', provinceId);
  const serialized = qs.toString();
  return serialized ? `?${serialized}` : '';
}

/** GET /v1/masters/courts */
export async function listCourts(query: CourtListQuery = {}): Promise<ApiResult<CourtListRow[]>> {
  const res = await caseApiRequest<unknown>(`/v1/masters/courts${buildCourtListQuery(query)}`, {
    method: 'GET',
  });
  if (!res.ok) return { success: false, message: res.message };
  const rows = unwrapRows(res.data)
    .map(mapCourtListRow)
    .filter((row): row is CourtListRow => row != null);
  return { success: true, data: rows };
}

/** GET /v1/masters/courts/{id} */
export async function getCourtById(id: string): Promise<ApiResult<CourtDetail>> {
  const courtId = id.trim();
  if (!courtId) return { success: false, message: 'Court id is missing.' };
  const res = await caseApiRequest<unknown>(`/v1/masters/courts/${encodeURIComponent(courtId)}`, {
    method: 'GET',
  });
  if (!res.ok) return { success: false, message: res.message };
  const detail = mapCourtDetail(
    res.data && typeof res.data === 'object' ? (res.data as ApiCourtRow) : {},
  );
  if (!detail) return { success: false, message: 'Court details are unavailable.' };
  return { success: true, data: detail };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseDivisionIdForApi(divisionId: string): string | undefined {
  const trimmed = divisionId.trim();
  if (!trimmed || !UUID_RE.test(trimmed)) return undefined;
  return trimmed;
}

/** POST /v1/masters/courts */
export async function createCourt(input: CreateCourtInput): Promise<ApiResult<void>> {
  const divisionId = parseDivisionIdForApi(input.divisionId);
  if (divisionId === undefined) return { success: false, message: 'Division id is invalid.' };
  const res = await caseApiRequest<unknown>('/v1/masters/courts', {
    method: 'POST',
    body: {
      name: input.name.trim(),
      active: input.active,
      court_type_id: input.courtTypeId.trim(),
      division_id: divisionId,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** PUT /v1/masters/courts/{id} */
export async function updateCourt(id: string, input: UpdateCourtInput): Promise<ApiResult<void>> {
  const courtId = id.trim();
  if (!courtId) return { success: false, message: 'Court id is missing.' };
  const divisionId = parseDivisionIdForApi(input.divisionId);
  if (divisionId === undefined) return { success: false, message: 'Division id is invalid.' };
  const res = await caseApiRequest<unknown>(`/v1/masters/courts/${encodeURIComponent(courtId)}`, {
    method: 'PUT',
    body: {
      name: input.name.trim(),
      active: input.active,
      court_type_id: input.courtTypeId.trim(),
      division_id: divisionId,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

function unwrapLookupRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.content)) return record.content as Record<string, unknown>[];
  if (Array.isArray(record.data)) return record.data as Record<string, unknown>[];
  return [];
}

function mapDistrictDivisionRow(row: Record<string, unknown>): LookupItem | null {
  const name = str(row.name);
  const id = str(row.id);
  if (!id || !name) return null;
  if (row.active === false) return null;
  return { id, name };
}

/** GET /v1/case-requests/district-divisions */
export async function listCourtMasterDivisions(): Promise<ApiResult<LookupItem[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-requests/district-divisions', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  const rows = unwrapLookupRows(res.data)
    .map(mapDistrictDivisionRow)
    .filter((row): row is LookupItem => row != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  return { success: true, data: rows };
}

/** GET /court-types */
export async function listCourtMasterCourtTypes(): Promise<ApiResult<LookupItem[]>> {
  const res = await caseApiRequest<unknown>('/court-types', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: parseCourtTypesFromApi(res.data) };
}

export { getProvinces, getDistricts };
